import { z } from 'zod'

import { DateZod, GeoPointZod, GoogleDistanceMatrixElementZod } from '../common'

const TransportRequestLocationZod = z.object({
  placeId: z.string().optional(),
  geolocation: GeoPointZod,
  formattedAddress: z.string(),
  addressComponents: z
    .array(
      z.object({
        long_name: z.string(),
        short_name: z.string(),
        types: z.string().array(),
      })
    )
    .optional()
    .default([]),
  piloteLocationId: z.number().optional(),
  piloteLocationName: z.string().optional(),
})

const MobileTransportRequestLocationZod = TransportRequestLocationZod.extend({
  name: z.string().optional(),
  apartment: z.string().optional(),
})

const TeamVolunteerZod = z
  .object({
    phoneNumber: z.string().optional(),
  })
  .optional()

export const TransportRequestDonationStatusZod = z.enum(['NOT_STARTED', 'SMS_SENT', 'SMS_LINK_CLICKED', 'COMPLETE'])

export const BaseTransportRequestZod = z.object({
  status: z.enum([
    'allocated',
    'assigned',
    'cancelled',
    'completed',
    'error',
    'on_board',
    'pending',
    'submitted',
    'transferred',
    'unfindable',
  ]),
  centralId: z.number(),
  eveningId: z.number(),
  piloteId: z.number(),
  regionId: z.string(),
  version: z.number().optional().default(0),
  language: z.string().default('fr'),
  customerId: z.string(),
  customerFirstName: z.string(),
  customerLastName: z.string(),
  customerPhoneNumber: z.string(),
  additionalInformation: z.string().optional().default(''),
  internalNote: z.string().optional().default(''),
  numberOfPassengers: z.number(),
  customerVehicle: z.object({
    manufacturer: z.string().optional(),
    model: z.string().optional(),
    color: z.number().optional(),
    seats: z.number().optional(),
    manualTransmission: z.boolean().optional(),
    breathalizer: z.boolean().optional(),
  }),

  team: z
    .object({
      id: z.string(),
      volunteersPhoneNumbers: z.string().array().optional().default([]),
      location: GeoPointZod.optional().nullable(),
      driver: TeamVolunteerZod,
      partner: TeamVolunteerZod,
      escort: TeamVolunteerZod,
    })
    .optional(),
  teamArrivedAtPickup: z.boolean().optional().default(false),

  donation: z
    .object({
      status: TransportRequestDonationStatusZod,
      value: z.number(),
    })
    .optional(),

  submitTimestamp: DateZod,
  assignmentTimestamp: DateZod.optional(),
  allocationTimestamp: DateZod.optional(),
  onboardTimestamp: DateZod.optional(),
  completionTimestamp: DateZod.optional(),
  evaluatedDistance: z
    .object({
      distanceMatrixResult: GoogleDistanceMatrixElementZod,
      durationInSeconds: z.number(),
      distanceInMeters: z.number(),
    })
    .optional(),
  expectedWaitTime: z
    .object({
      minimumInMinutes: z.number(),
      maximumInMinutes: z.number(),
      averageInMinutes: z.number(),
    })
    .optional(),

  assignedBy: z.number().nullable().optional(),

  availableForAutomation: z.boolean().optional(),
  plannedTeamAssignment: z
    .object({
      teamId: z.string().nullable(),
      planId: z.string(),
      willExecuteAt: DateZod.optional(),
    })
    .optional(),

  pendingEvent: z.string().optional(),
  teamPendingEvent: z
    .union([
      z.object({
        type: z.string(),
        timestamp: DateZod,
      }),
      z.string(),
    ])
    .optional(),
  dashboardPendingRequest: z
    .object({
      type: z.string(),
      timestamp: DateZod,
      payload: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),

  timestamp: DateZod,
  cancellationReason: z.enum(['teamArrivingTooEarly', 'teamArrivingTooLate', 'foundOtherSolution', 'other']).optional(),
})

export const MobileTransportRequestZod = BaseTransportRequestZod.extend({
  type: z.literal('mobile'),
  piloteId: z.number().optional(),
  origin: MobileTransportRequestLocationZod,
  destination: MobileTransportRequestLocationZod,
})

export const PiloteConvertedTransportRequestZod = BaseTransportRequestZod.extend({
  type: z.literal('pilote'),
  // pilote transport requests may not have a customerId
  customerId: z.string().optional(),
  origin: TransportRequestLocationZod,
  destination: TransportRequestLocationZod,
})

const DraftTransportRequestZod = MobileTransportRequestZod.partial().extend({
  type: z.literal('draft'),
  // override because of partial MobileTransportRequestZod
  status: z.enum(['draft', 'deleted_draft']),
  // override because of partial MobileTransportRequestZod
  customerId: z.string(),
  origin: MobileTransportRequestLocationZod.extend({ error: z.string() }).partial().optional(),
  destination: MobileTransportRequestLocationZod.extend({ error: z.string() }).partial().optional(),
  error: z.string().optional(),
})

export const TransportRequestZod = z.discriminatedUnion('type', [
  DraftTransportRequestZod,
  MobileTransportRequestZod,
  PiloteConvertedTransportRequestZod,
])

export type TransportRequest = z.infer<typeof TransportRequestZod>

export type MobileTransportRequest = Extract<TransportRequest, { type: 'mobile' }>
// eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
export type MobileTransportRequestLocation = MobileTransportRequest['origin'] | MobileTransportRequest['destination']
